import { useMemo, useState } from 'react';

import { PlatformBasicError } from '@cloudquery/api-platform';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { captureError, assertUnreachable } from 'shared/base';

import { FilterInputConfigError } from './filterInputConfigError';
import { FilterInputProps } from './types';
import { useFetchAllQueries } from '../../../hooks/useFetchAllQueries';

type Props = FilterInputProps & {
  multiple?: boolean;
  options?: { label: string; value: any }[];
  query?: string;
};

interface FilterOption {
  expression: string;
  label: string;
  value: string;
}

const DEFAULT_FILTER_OPTION: FilterOption = {
  expression: '',
  label: 'All',
  value: 'All',
};

const autocompleteFilterOptions = createFilterOptions<FilterOption>({
  limit: 500,
});

export const ReportGenericFilterSelect = ({
  filterExpression,
  filterLabel,
  multiple = false,
  onChange,
  options,
  query,
  selectedFilter,
}: Props) => {
  const [error, setError] = useState<string>();
  const {
    data,
    error: errorData,
    status,
  } = useFetchAllQueries(
    {
      baseQuery: query || 'SELECT 1',
      params: {
        per_page: 1000,
      },
    },
    {
      enabled: !options,
    },
  );

  const filterOptions = useMemo(() => {
    if (options) {
      return options.map((option) => ({
        expression: filterExpression?.replace('?', option.value as string),
        label: option.label,
        value: option.value,
      }));
    }

    switch (status) {
      case 'loading': {
        return [DEFAULT_FILTER_OPTION];
      }
      case 'error': {
        const { data: { message } = { message: '' } } = errorData as PlatformBasicError & {
          data: { message: string };
        };
        const missingTable = message.match(/Unknown table expression identifier '(.*?)'/);
        if (missingTable) {
          setError(`The ${missingTable[1]} table is missing.`);
        } else {
          setError(`Invalid query`);
        }

        captureError(`Platform reports error: Invalid filter query: ${message}`);

        return;
      }
      case 'success': {
        return [
          DEFAULT_FILTER_OPTION,
          ...(data.data.rows as Array<[any, any]>).map(([value, name]) => ({
            expression: filterExpression?.replace('?', value as string),
            label: (name ?? value) as string,
            value: value as string,
          })),
        ];
      }
      default: {
        assertUnreachable(status);
      }
    }
  }, [options, status, filterExpression, errorData, data]);

  const handleOnChange = (_: any, value: FilterOption | FilterOption[] | null) => {
    if (multiple) {
      if (Array.isArray(value)) {
        const nonEmptyValues = value.filter((v) => v.expression !== '');
        if (nonEmptyValues.length === 0) {
          onChange(null);
        } else {
          const values = nonEmptyValues.map((v) => `'${v.value}'`).join(', ');
          const completeExpression = filterExpression.replace('?', values);
          onChange(completeExpression);
        }
      } else {
        onChange(null);
      }
    } else {
      if (value === null || (value as FilterOption).expression === null) {
        onChange(null);
      } else {
        onChange((value as FilterOption).expression);
      }
    }
  };

  switch (status) {
    case 'error': {
      return <FilterInputConfigError filterLabel={filterLabel} message={error!} type={'generic'} />;
    }

    case 'loading':
    case 'success': {
      return (
        <FormControl>
          <Autocomplete
            filterOptions={autocompleteFilterOptions}
            getOptionLabel={(option) => option?.label || ''}
            limitTags={-1}
            multiple={multiple}
            onChange={handleOnChange}
            options={filterOptions || []}
            renderInput={(params) => (
              <TextField
                {...params}
                label={filterLabel}
                placeholder={multiple ? '' : filterLabel}
                size="small"
                sx={{
                  '& .MuiFilledInput-root': {
                    flexWrap: multiple ? 'nowrap' : undefined,
                    maxWidth: multiple ? 200 : undefined,
                    overflowX: multiple ? 'auto' : undefined,
                  },
                  '& .MuiInputBase-adornedEnd': {
                    position: multiple ? 'static' : undefined,
                  },
                }}
                variant="filled"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={props.key} style={{ fontSize: 12 }}>
                <Stack>{option.label}</Stack>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  size="small"
                  sx={{
                    '& .MuiChip-label': {
                      display: 'block',
                      maxWidth: '100px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                    maxWidth: '120px',
                  }}
                />
              ))
            }
            sx={(theme) => ({
              '& .MuiAutocomplete-tag': {
                fontSize: theme.typography.caption.fontSize,
                margin: 0,
                maxHeight: '20px',
              },
              minWidth: 160,
            })}
            value={
              multiple
                ? (() => {
                    if (!selectedFilter || !filterOptions) return [];

                    // Extract values from complete expression like "field IN ('val1', 'val2')"
                    const match = selectedFilter.match(/IN\s*\(([^)]+)\)/i);
                    if (!match) return [];
                    const selectedValues = match[1]
                      .split(',')
                      .map((v) => v.trim().replace(/^'|'$/g, ''));

                    return filterOptions.filter((opt) => selectedValues.includes(opt.value));
                  })()
                : filterOptions?.find((opt) => opt.expression === selectedFilter) || null
            }
          />
        </FormControl>
      );
    }
  }
};
